/*
 * Copyright (c) 2026 Paxia AI Pty Ltd. All rights reserved.
 * This software is proprietary and confidential.
 * No part of this codebase may be copied, modified, published, distributed, sublicensed, or used without prior written permission from Paxia AI Pty Ltd.
 */

/**
 * 3D neural-network visualization using vanilla three.js.
 *
 * Renders each layer as a grid of sphere "neurons" on a plane and draws
 * translucent lines between consecutive fully-connected layers. The camera
 * auto-rotates and supports click-drag to orbit.
 */

import React, { useEffect, useMemo, useRef, useState } from 'react';
import * as THREE from 'three';
import { Box, IconButton, Paper, Stack, Tooltip, Typography } from '@mui/material';
import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import PauseIcon from '@mui/icons-material/Pause';
import RestartAltIcon from '@mui/icons-material/RestartAlt';

export interface Layer3DSpec {
  label: string;
  units: number;
  color: string;
  kind?: string;
  detail?: string;
  /** Optional per-neuron labels (aligned with units, up to 64 shown). */
  neuronLabels?: string[];
  /** Optional per-neuron numeric value (e.g., feature importance, weight). */
  neuronValues?: number[];
  /** Format hint for neuronValues: 'percent' | 'number' | 'prob'. */
  valueFormat?: 'percent' | 'number' | 'prob';
  /** Unit suffix for value display (e.g., 'importance'). */
  valueUnit?: string;
}

interface Props {
  layers: Layer3DSpec[];
  height?: number;
}

// Clamp very large layers so the scene stays readable.
const MAX_NEURONS_PER_LAYER = 64;

const gridFor = (units: number): { cols: number; rows: number } => {
  const n = Math.min(units, MAX_NEURONS_PER_LAYER);
  const cols = Math.ceil(Math.sqrt(n));
  const rows = Math.ceil(n / cols);
  return { cols, rows };
};

const NeuralNetwork3D: React.FC<Props> = ({ layers, height = 380 }) => {
  const mountRef = useRef<HTMLDivElement | null>(null);
  const tooltipRef = useRef<HTMLDivElement | null>(null);
  const rotatingRef = useRef(false);
  const [rotating, setRotating] = useState(false);
  const resetSignalRef = useRef(0);
  const [resetKey, setResetKey] = useState(0);
  const [pinned, setPinned] = useState<null | {
    layer: Layer3DSpec;
    layerIndex: number;
    neuronIndex: number;
    totalUnits: number;
  }>(null);

  // Stringify layer spec so effect only re-runs when layers change structurally.
  const layersKey = useMemo(
    () =>
      layers
        .map((l) => `${l.label}:${l.units}:${l.color}`)
        .join('|'),
    [layers],
  );

  useEffect(() => {
    rotatingRef.current = rotating;
  }, [rotating]);

  useEffect(() => {
    const mount = mountRef.current;
    if (!mount) return;

    const width = mount.clientWidth;
    const h = height;

    const scene = new THREE.Scene();
    scene.background = new THREE.Color(0x0b1020);

    const camera = new THREE.PerspectiveCamera(45, width / h, 0.1, 1000);
    const renderer = new THREE.WebGLRenderer({ antialias: true, alpha: true });
    renderer.setPixelRatio(window.devicePixelRatio);
    renderer.setSize(width, h);
    mount.appendChild(renderer.domElement);

    // Lights
    scene.add(new THREE.AmbientLight(0xffffff, 0.55));
    const dirLight = new THREE.DirectionalLight(0xffffff, 0.65);
    dirLight.position.set(5, 10, 7.5);
    scene.add(dirLight);

    // Build neuron positions per layer.
    const layerSpacing = 6;
    const cellSpacing = 1.3;
    const neuronRadius = 0.35;

    const layerPositions: THREE.Vector3[][] = [];
    const group = new THREE.Group();
    // Track each instanced mesh so raycasting can map a hit back to layer+neuron.
    const neuronMeshes: {
      mesh: THREE.InstancedMesh;
      layerIndex: number;
      totalUnits: number;
      baseColor: THREE.Color;
    }[] = [];

    layers.forEach((layer, idx) => {
      const { cols, rows } = gridFor(layer.units);
      const count = Math.min(layer.units, MAX_NEURONS_PER_LAYER);
      const offsetX = -((cols - 1) * cellSpacing) / 2;
      const offsetY = -((rows - 1) * cellSpacing) / 2;
      const z = (idx - (layers.length - 1) / 2) * layerSpacing;

      const positions: THREE.Vector3[] = [];
      const color = new THREE.Color(layer.color);

      // Instanced neurons (faster for many spheres).
      const sphereGeo = new THREE.SphereGeometry(neuronRadius, 16, 16);
      const material = new THREE.MeshStandardMaterial({
        color,
        emissive: color.clone().multiplyScalar(0.3),
        roughness: 0.4,
        metalness: 0.2,
      });
      const mesh = new THREE.InstancedMesh(sphereGeo, material, count);

      const dummy = new THREE.Object3D();
      let i = 0;
      for (let r = 0; r < rows && i < count; r++) {
        for (let c = 0; c < cols && i < count; c++) {
          const x = offsetX + c * cellSpacing;
          const y = offsetY + r * cellSpacing;
          dummy.position.set(x, y, z);
          dummy.updateMatrix();
          mesh.setMatrixAt(i, dummy.matrix);
          positions.push(new THREE.Vector3(x, y, z));
          i++;
        }
      }
      mesh.instanceMatrix.needsUpdate = true;
      // Initialize per-instance color so hover highlighting via setColorAt works.
      for (let j = 0; j < count; j++) {
        mesh.setColorAt(j, color);
      }
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      mesh.userData = { layerIndex: idx };
      group.add(mesh);
      neuronMeshes.push({
        mesh,
        layerIndex: idx,
        totalUnits: layer.units,
        baseColor: color.clone(),
      });

      // Layer label plane (transparent disc behind neurons).
      const planeGeo = new THREE.PlaneGeometry(
        cols * cellSpacing + 2,
        rows * cellSpacing + 2,
      );
      const planeMat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.04,
        side: THREE.DoubleSide,
      });
      const plane = new THREE.Mesh(planeGeo, planeMat);
      plane.position.set(0, 0, z);
      group.add(plane);

      // Outline ring
      const ringGeo = new THREE.RingGeometry(
        Math.max(cols, rows) * cellSpacing * 0.55,
        Math.max(cols, rows) * cellSpacing * 0.56,
        64,
      );
      const ringMat = new THREE.MeshBasicMaterial({
        color,
        transparent: true,
        opacity: 0.35,
        side: THREE.DoubleSide,
      });
      const ring = new THREE.Mesh(ringGeo, ringMat);
      ring.position.set(0, 0, z - 0.01);
      group.add(ring);

      layerPositions.push(positions);
    });

    // Draw connection lines between consecutive layers (sampled if huge).
    for (let i = 0; i < layerPositions.length - 1; i++) {
      const from = layerPositions[i];
      const to = layerPositions[i + 1];
      if (!from.length || !to.length) continue;

      // Limit total lines per gap to keep rendering smooth.
      const maxLines = 350;
      const total = from.length * to.length;
      const step = Math.max(1, Math.ceil(total / maxLines));

      const positions: number[] = [];
      let idx = 0;
      for (let a = 0; a < from.length; a++) {
        for (let b = 0; b < to.length; b++) {
          if (idx % step === 0) {
            const p1 = from[a];
            const p2 = to[b];
            positions.push(p1.x, p1.y, p1.z, p2.x, p2.y, p2.z);
          }
          idx++;
        }
      }

      const geom = new THREE.BufferGeometry();
      geom.setAttribute(
        'position',
        new THREE.Float32BufferAttribute(positions, 3),
      );
      const mat = new THREE.LineBasicMaterial({
        color: 0x88aaff,
        transparent: true,
        opacity: 0.12,
      });
      const lines = new THREE.LineSegments(geom, mat);
      group.add(lines);
    }

    scene.add(group);

    // Frame the network.
    const totalDepth = (layers.length - 1) * layerSpacing;
    const maxDim = Math.max(
      ...layers.map((l) => Math.max(gridFor(l.units).cols, gridFor(l.units).rows)),
      4,
    );
    const frameRadius = Math.max(totalDepth * 0.7, maxDim * cellSpacing);
    camera.position.set(frameRadius * 1.2, frameRadius * 0.55, frameRadius * 1.4);
    camera.lookAt(0, 0, 0);

    // Simple orbit controls (mouse drag + wheel) — no extra dep.
    let isDragging = false;
    let dragMoved = false;
    let prevX = 0;
    let prevY = 0;
    let yaw = Math.atan2(camera.position.x, camera.position.z);
    let pitch = Math.asin(
      camera.position.y /
        Math.sqrt(
          camera.position.x ** 2 + camera.position.y ** 2 + camera.position.z ** 2,
        ),
    );
    let radius = camera.position.length();

    const updateCamera = () => {
      pitch = Math.max(-Math.PI / 2 + 0.1, Math.min(Math.PI / 2 - 0.1, pitch));
      camera.position.x = radius * Math.cos(pitch) * Math.sin(yaw);
      camera.position.y = radius * Math.sin(pitch);
      camera.position.z = radius * Math.cos(pitch) * Math.cos(yaw);
      camera.lookAt(0, 0, 0);
    };

    const onMouseDown = (e: MouseEvent) => {
      isDragging = true;
      dragMoved = false;
      prevX = e.clientX;
      prevY = e.clientY;
    };
    const onMouseUp = () => {
      isDragging = false;
    };
    const onMouseMove = (e: MouseEvent) => {
      if (isDragging) {
        const dx = e.clientX - prevX;
        const dy = e.clientY - prevY;
        if (Math.abs(dx) + Math.abs(dy) > 2) dragMoved = true;
        prevX = e.clientX;
        prevY = e.clientY;
        yaw -= dx * 0.01;
        pitch += dy * 0.01;
        updateCamera();
        return;
      }
      // Hover raycasting
      handleHover(e);
    };
    const onWheel = (e: WheelEvent) => {
      e.preventDefault();
      radius = Math.max(4, Math.min(200, radius + e.deltaY * 0.05));
      updateCamera();
    };

    // Raycasting state
    const raycaster = new THREE.Raycaster();
    const ndc = new THREE.Vector2();
    let hoveredNeuron: {
      mesh: THREE.InstancedMesh;
      layerIndex: number;
      neuronIndex: number;
      baseColor: THREE.Color;
    } | null = null;

    const restoreHovered = () => {
      if (!hoveredNeuron) return;
      const { mesh, neuronIndex, baseColor } = hoveredNeuron;
      mesh.setColorAt(neuronIndex, baseColor);
      if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
      hoveredNeuron = null;
    };

    const highlightColor = new THREE.Color('#ffffff');

    const hideTooltip = () => {
      const tip = tooltipRef.current;
      if (tip) tip.style.display = 'none';
    };

    const showTooltip = (
      x: number,
      y: number,
      layer: Layer3DSpec,
      layerIndex: number,
      neuronIndex: number,
      totalUnits: number,
    ) => {
      const tip = tooltipRef.current;
      if (!tip) return;
      const shown = Math.min(totalUnits, MAX_NEURONS_PER_LAYER);
      const hiddenNote =
        totalUnits > MAX_NEURONS_PER_LAYER
          ? `<div style="opacity:.65;margin-top:2px">showing ${shown} of ${totalUnits} neurons</div>`
          : '';
      const detail = layer.detail
        ? `<div style="opacity:.75;margin-top:2px">${layer.detail}</div>`
          : '';
      const kind = layer.kind
        ? `<div style="opacity:.6;font-size:10px;text-transform:uppercase;letter-spacing:.5px">${layer.kind}</div>`
        : '';
      // Per-neuron label + value
      const neuronLabel = layer.neuronLabels?.[neuronIndex];
      const neuronValue = layer.neuronValues?.[neuronIndex];
      let valueStr = '';
      if (neuronValue != null && Number.isFinite(neuronValue)) {
        if (layer.valueFormat === 'percent') {
          valueStr = `${(neuronValue * 100).toFixed(2)}%`;
        } else if (layer.valueFormat === 'prob') {
          valueStr = neuronValue.toFixed(4);
        } else {
          valueStr = Math.abs(neuronValue) < 1 ? neuronValue.toFixed(4) : neuronValue.toFixed(3);
        }
      }
      const neuronLine = neuronLabel
        ? `<div style="margin-top:4px;padding-top:4px;border-top:1px solid rgba(255,255,255,0.12)">
             <div style="font-weight:600">${neuronLabel}</div>
             ${valueStr ? `<div style="opacity:.85">${layer.valueUnit ? layer.valueUnit + ': ' : ''}${valueStr}</div>` : ''}
           </div>`
        : valueStr
          ? `<div style="margin-top:4px;opacity:.85">${layer.valueUnit ? layer.valueUnit + ': ' : 'Value: '}${valueStr}</div>`
          : '';
      tip.innerHTML = `
        <div style="display:flex;align-items:center;gap:6px;margin-bottom:2px">
          <span style="display:inline-block;width:10px;height:10px;border-radius:50%;background:${layer.color}"></span>
          <b>${layer.label}</b>
        </div>
        ${kind}
        <div>Neuron #${neuronIndex + 1} / ${totalUnits}</div>
        <div style="opacity:.75">Layer ${layerIndex + 1} of ${layers.length}</div>
        ${detail}
        ${neuronLine}
        ${hiddenNote}
      `;
      tip.style.display = 'block';
      // Position relative to mount container.
      const rect = renderer.domElement.getBoundingClientRect();
      const localX = x - rect.left;
      const localY = y - rect.top;
      const tipRect = tip.getBoundingClientRect();
      const maxX = rect.width - tipRect.width - 8;
      const maxY = rect.height - tipRect.height - 8;
      tip.style.left = `${Math.max(4, Math.min(localX + 12, maxX))}px`;
      tip.style.top = `${Math.max(4, Math.min(localY + 12, maxY))}px`;
    };

    const handleHover = (e: MouseEvent) => {
      const rect = renderer.domElement.getBoundingClientRect();
      ndc.x = ((e.clientX - rect.left) / rect.width) * 2 - 1;
      ndc.y = -((e.clientY - rect.top) / rect.height) * 2 + 1;
      raycaster.setFromCamera(ndc, camera);
      const meshes = neuronMeshes.map((n) => n.mesh);
      const hits = raycaster.intersectObjects(meshes, false);
      if (!hits.length || hits[0].instanceId == null) {
        if (hoveredNeuron) {
          restoreHovered();
          hideTooltip();
        }
        renderer.domElement.style.cursor = isDragging ? 'grabbing' : 'grab';
        return;
      }
      const hit = hits[0];
      const entry = neuronMeshes.find((n) => n.mesh === hit.object);
      if (!entry) return;
      const neuronIndex = hit.instanceId!;
      const sameAsBefore =
        hoveredNeuron &&
        hoveredNeuron.mesh === entry.mesh &&
        hoveredNeuron.neuronIndex === neuronIndex;
      if (!sameAsBefore) {
        restoreHovered();
        entry.mesh.setColorAt(neuronIndex, highlightColor);
        if (entry.mesh.instanceColor) entry.mesh.instanceColor.needsUpdate = true;
        hoveredNeuron = {
          mesh: entry.mesh,
          layerIndex: entry.layerIndex,
          neuronIndex,
          baseColor: entry.baseColor,
        };
      }
      renderer.domElement.style.cursor = 'pointer';
      showTooltip(
        e.clientX,
        e.clientY,
        layers[entry.layerIndex],
        entry.layerIndex,
        neuronIndex,
        entry.totalUnits,
      );
    };

    const onMouseLeave = () => {
      if (hoveredNeuron) restoreHovered();
      hideTooltip();
    };

    const onClick = (_e: MouseEvent) => {
      if (dragMoved) return; // don't treat drag-release as click
      if (hoveredNeuron) {
        const h = hoveredNeuron;
        setPinned({
          layer: layers[h.layerIndex],
          layerIndex: h.layerIndex,
          neuronIndex: h.neuronIndex,
          totalUnits: neuronMeshes.find((n) => n.mesh === h.mesh)?.totalUnits || 0,
        });
      } else {
        setPinned(null);
      }
    };

    renderer.domElement.addEventListener('mousedown', onMouseDown);
    window.addEventListener('mouseup', onMouseUp);
    window.addEventListener('mousemove', onMouseMove);
    renderer.domElement.addEventListener('wheel', onWheel, { passive: false });
    renderer.domElement.addEventListener('mouseleave', onMouseLeave);
    renderer.domElement.addEventListener('click', onClick);

    // Animate.
    let frameId = 0;
    const clock = new THREE.Clock();
    const initialYaw = yaw;
    let signalCount = resetSignalRef.current;

    const animate = () => {
      frameId = requestAnimationFrame(animate);
      const delta = clock.getDelta();
      if (!isDragging && rotatingRef.current) {
        yaw += delta * 0.3;
        updateCamera();
      }
      // Handle reset.
      if (resetSignalRef.current !== signalCount) {
        signalCount = resetSignalRef.current;
        yaw = initialYaw;
        pitch = 0.35;
        radius = frameRadius * 1.8;
        updateCamera();
      }
      renderer.render(scene, camera);
    };
    animate();

    // Resize handling.
    const onResize = () => {
      if (!mount) return;
      const w = mount.clientWidth;
      renderer.setSize(w, h);
      camera.aspect = w / h;
      camera.updateProjectionMatrix();
    };
    const resizeObserver = new ResizeObserver(onResize);
    resizeObserver.observe(mount);

    return () => {
      cancelAnimationFrame(frameId);
      resizeObserver.disconnect();
      renderer.domElement.removeEventListener('mousedown', onMouseDown);
      window.removeEventListener('mouseup', onMouseUp);
      window.removeEventListener('mousemove', onMouseMove);
      renderer.domElement.removeEventListener('wheel', onWheel);
      renderer.domElement.removeEventListener('mouseleave', onMouseLeave);
      renderer.domElement.removeEventListener('click', onClick);
      scene.traverse((obj: any) => {
        if (obj.geometry) obj.geometry.dispose?.();
        if (obj.material) {
          if (Array.isArray(obj.material)) {
            obj.material.forEach((m: any) => m.dispose?.());
          } else {
            obj.material.dispose?.();
          }
        }
      });
      renderer.dispose();
      if (renderer.domElement.parentNode === mount) {
        mount.removeChild(renderer.domElement);
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [layersKey, height, resetKey]);

  return (
    <Paper variant="outlined" sx={{ p: 1.5, position: 'relative' }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 1 }}>
        <Typography variant="overline" color="text.secondary">
          3D Network
        </Typography>
        <Box sx={{ flex: 1 }} />
        <Tooltip title={rotating ? 'Pause rotation' : 'Resume rotation'}>
          <IconButton size="small" onClick={() => setRotating((r) => !r)}>
            {rotating ? <PauseIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
          </IconButton>
        </Tooltip>
        <Tooltip title="Reset view">
          <IconButton
            size="small"
            onClick={() => {
              resetSignalRef.current += 1;
              setResetKey((k) => k + 1);
            }}
          >
            <RestartAltIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      <Box
        sx={{
          position: 'relative',
          width: '100%',
          height,
          borderRadius: 1,
          overflow: 'hidden',
        }}
      >
        <Box
          ref={mountRef}
          sx={{
            width: '100%',
            height: '100%',
            cursor: 'grab',
            '&:active': { cursor: 'grabbing' },
          }}
        />
        {/* Hover tooltip (positioned via JS inside the canvas effect) */}
        <Box
          ref={tooltipRef}
          sx={{
            position: 'absolute',
            display: 'none',
            pointerEvents: 'none',
            bgcolor: 'rgba(20,24,40,0.92)',
            color: 'common.white',
            border: '1px solid rgba(255,255,255,0.15)',
            borderRadius: 1,
            px: 1,
            py: 0.5,
            fontSize: 12,
            lineHeight: 1.35,
            maxWidth: 260,
            zIndex: 2,
            boxShadow: 3,
          }}
        />
        {pinned && (
          <Paper
            elevation={4}
            sx={{
              position: 'absolute',
              left: 8,
              bottom: 8,
              p: 1,
              maxWidth: 280,
              bgcolor: 'rgba(20,24,40,0.95)',
              color: 'common.white',
              border: '1px solid rgba(255,255,255,0.15)',
              zIndex: 3,
            }}
          >
            <Stack direction="row" alignItems="center" spacing={0.75}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: '50%',
                  bgcolor: pinned.layer.color,
                }}
              />
              <Typography variant="subtitle2" sx={{ color: 'inherit' }}>
                {pinned.layer.label}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <IconButton
                size="small"
                onClick={() => setPinned(null)}
                sx={{ color: 'inherit', opacity: 0.7 }}
              >
                <Typography variant="caption">✕</Typography>
              </IconButton>
            </Stack>
            {pinned.layer.kind && (
              <Typography
                variant="caption"
                sx={{ display: 'block', opacity: 0.6, textTransform: 'uppercase', letterSpacing: 0.5 }}
              >
                {pinned.layer.kind}
              </Typography>
            )}
            <Typography variant="caption" sx={{ display: 'block' }}>
              Neuron #{pinned.neuronIndex + 1} / {pinned.totalUnits}
            </Typography>
            <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
              Layer {pinned.layerIndex + 1} of {layers.length}
            </Typography>
            {pinned.layer.detail && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.75 }}>
                {pinned.layer.detail}
              </Typography>
            )}
            {pinned.layer.neuronLabels?.[pinned.neuronIndex] && (
              <Typography variant="caption" sx={{ display: 'block', mt: 0.5, fontWeight: 600 }}>
                {pinned.layer.neuronLabels[pinned.neuronIndex]}
              </Typography>
            )}
            {pinned.layer.neuronValues?.[pinned.neuronIndex] != null && (
              <Typography variant="caption" sx={{ display: 'block', opacity: 0.85 }}>
                {pinned.layer.valueUnit ? `${pinned.layer.valueUnit}: ` : 'Value: '}
                {pinned.layer.valueFormat === 'percent'
                  ? `${(pinned.layer.neuronValues[pinned.neuronIndex] * 100).toFixed(2)}%`
                  : pinned.layer.neuronValues[pinned.neuronIndex].toFixed(4)}
              </Typography>
            )}
          </Paper>
        )}
      </Box>
      <Stack
        direction="row"
        spacing={1}
        flexWrap="wrap"
        useFlexGap
        sx={{ mt: 1 }}
      >
        {layers.map((l, i) => (
          <Stack
            key={i}
            direction="row"
            alignItems="center"
            spacing={0.5}
            sx={{ fontSize: 11 }}
          >
            <Box
              sx={{
                width: 10,
                height: 10,
                borderRadius: '50%',
                bgcolor: l.color,
              }}
            />
            <Typography variant="caption" color="text.secondary">
              {l.label} ({l.units})
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mt: 0.5 }}
      >
        Hover a neuron for details · click to pin · drag to orbit · scroll to zoom · ≤64 neurons shown per layer
      </Typography>
    </Paper>
  );
};

export default NeuralNetwork3D;
